/**
 * Graph Error Boundary Component
 *
 * React Error Boundary for catching and displaying component crashes
 * in the Ontology Graph Viewer.
 *
 * @module client/components/GraphErrorBoundary
 * @version 1.0
 * @date 2025-10-10
 * @see Documentation/Development/Design Docs/Graph Spec.md Section X
 */

import * as React from "react";

interface GraphErrorCardProps {
  error?: Error;
}

/**
 * Error display card shown when graph component crashes
 */
function GraphErrorCard({ error }: GraphErrorCardProps): React.ReactElement {
  return (
    <div className="graph-error-card">
      <div className="error-icon">⚠️</div>
      <h2>Graph Visualization Error</h2>
      <p className="error-message">
        The ontology graph could not be rendered due to an unexpected error.
      </p>
      {error && (
        <details className="error-details">
          <summary>Technical Details</summary>
          <pre className="error-stack">
            {error.name}: {error.message}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        </details>
      )}
      <div className="error-actions">
        <button
          className="btn-retry"
          onClick={() => window.location.reload()}
        >
          Reload Page
        </button>
        <button
          className="btn-download"
          onClick={() => {
            // Download raw JSON for debugging
            const jobId = window.location.pathname.split('/').pop();
            window.location.href = `/analysis/${jobId}/ontology-data`;
          }}
        >
          Download Raw Data
        </button>
      </div>
    </div>
  );
}

interface GraphErrorBoundaryProps {
  children: React.ReactNode;
}

interface GraphErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary for Graph Components
 *
 * Catches React component errors and displays a user-friendly error card.
 * Logs errors to telemetry for monitoring.
 *
 * Usage:
 * ```tsx
 * <GraphErrorBoundary>
 *   <OntologyGraphExplorer graph={graphSpec} />
 * </GraphErrorBoundary>
 * ```
 */
export class GraphErrorBoundary extends React.Component<
  GraphErrorBoundaryProps,
  GraphErrorBoundaryState
> {
  constructor(props: GraphErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): GraphErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Log to console for development
    console.error('[GraphErrorBoundary] Component crashed:', error, info);

    // Dispatch custom event for telemetry (picked up by analytics)
    window.dispatchEvent(
      new CustomEvent("graph_error", {
        detail: {
          error: String(error),
          errorInfo: info.componentStack,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        },
      })
    );

    // Could also send to error tracking service (Sentry, etc.)
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { contexts: { react: info } });
    // }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <GraphErrorCard error={this.state.error} />;
    }

    return this.props.children;
  }
}

// CSS for error card (inline styles for self-contained component)
// In production, move to external stylesheet

const styleTag = document.createElement('style');
styleTag.innerHTML = `
  .graph-error-card {
    max-width: 600px;
    margin: 4rem auto;
    padding: 2rem;
    background: var(--pane, #ffffff);
    border: 2px solid var(--error, #dc2626);
    border-radius: 8px;
    text-align: center;
  }

  .graph-error-card .error-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }

  .graph-error-card h2 {
    color: var(--error, #dc2626);
    margin-bottom: 1rem;
  }

  .graph-error-card .error-message {
    color: var(--text, #1f2937);
    margin-bottom: 1.5rem;
  }

  .graph-error-card .error-details {
    text-align: left;
    margin: 1.5rem 0;
    padding: 1rem;
    background: var(--canvas-bg, #fafbfc);
    border: 1px solid var(--line, #d1d5db);
    border-radius: 4px;
  }

  .graph-error-card .error-details summary {
    cursor: pointer;
    font-weight: 600;
    user-select: none;
  }

  .graph-error-card .error-stack {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: var(--muted, #6b7280);
    overflow-x: auto;
  }

  .graph-error-card .error-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    margin-top: 1.5rem;
  }

  .graph-error-card .btn-retry,
  .graph-error-card .btn-download {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .graph-error-card .btn-retry {
    background: var(--primary, #3b82f6);
    color: white;
  }

  .graph-error-card .btn-retry:hover {
    background: var(--primary-dark, #2563eb);
  }

  .graph-error-card .btn-download {
    background: var(--secondary, #6b7280);
    color: white;
  }

  .graph-error-card .btn-download:hover {
    background: var(--secondary-dark, #4b5563);
  }
`;

// Only inject styles once
if (!document.querySelector('style[data-graph-error-boundary]')) {
  styleTag.setAttribute('data-graph-error-boundary', 'true');
  document.head.appendChild(styleTag);
}
